/** Auth por dispositivo para el modo offline.
 *  Al iniciar sesión con internet se guarda un "perfil de dispositivo" (usuario
 *  + token) y, opcionalmente, un PIN. Sin internet, el PIN permite restaurar la
 *  sesión localmente para seguir vendiendo. El PIN se guarda HASHEADO
 *  (SHA-256 + salt), nunca en texto plano. */

const KEY = 'deviceProfile';

export interface DeviceProfile {
  email: string;
  user: any;          // objeto user completo devuelto por el login
  token: string;      // último token válido (para operar offline)
  pinSalt?: string;
  pinHash?: string;
  savedAt: number;
}

export function getDeviceProfile(): DeviceProfile | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) as DeviceProfile : null;
  } catch { return null; }
}

function save(p: DeviceProfile) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* ignora cuota */ }
}

/** Guarda/actualiza el perfil tras un login con internet (conserva el PIN). */
export function saveSession(user: any, token: string): void {
  const prev = getDeviceProfile();
  save({
    email: user?.email || prev?.email || '',
    user, token,
    pinSalt: prev?.pinSalt,
    pinHash: prev?.pinHash,
    savedAt: Date.now(),
  });
}

export function clearDeviceProfile(): void {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

export function hasPin(): boolean {
  const p = getDeviceProfile();
  return !!(p && p.pinHash && p.pinSalt);
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function hashPin(pin: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}:${pin}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return toHex(digest);
}

/** Define (o cambia) el PIN del dispositivo. Requiere un perfil ya guardado. */
export async function setPin(pin: string): Promise<boolean> {
  const p = getDeviceProfile();
  if (!p) return false;
  const saltBytes = crypto.getRandomValues(new Uint8Array(16));
  const salt = toHex(saltBytes.buffer);
  p.pinSalt = salt;
  p.pinHash = await hashPin(pin, salt);
  save(p);
  return true;
}

/** Verifica el PIN contra el hash guardado. */
export async function verifyPin(pin: string): Promise<boolean> {
  const p = getDeviceProfile();
  if (!p || !p.pinHash || !p.pinSalt) return false;
  const h = await hashPin(pin, p.pinSalt);
  return h === p.pinHash;
}
